import {
  wallOpeningOpening2,
  wallOpeningOpening3,
  wallOpeningShouldMirror,
} from "building-common/openings/openingQuery";
import { profile2OrientCopy } from "building-common/profile2Utils";
import { getWallPath } from "building-generation/elements/walls/wall";
import type { Block } from "building-types/blocks/blockTypes";
import type { LevelOpenFullDoors } from "building-types/doors/levelOpenFullDoors";
import type { Label } from "building-types/facades/label";
import { LabelPrecedence } from "building-types/facades/label";
import type { LevelDef } from "building-types/levels/LevelDef";
import { stackedLevelPlacementNew, type GeneratedLevelDatums, type Level } from "building-types/levels/levelTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type {
  HalfEdgeAssociationTable,
  RWHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { GableRoofVerticalRails } from "building-types/roofs/gableRoofExtrusionTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { GableLabel } from "building-types/roofs/roofSystemTypes";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { RoofWallSystemEdge } from "building-types/roofs/roofWallSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoomConnection } from "building-types/rooms/roomTraversalTypes";
import { ROOF_VIRTUAL_ROOM_NAME, type Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { RoofExtensions, RoofPerimeters, ShedRoofExtensions } from "building-types/settings/roofTypes";
import { RoofFaces, RoofSide } from "building-types/settings/roofTypes";
import type { RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import { GableRoofType, RoofWallMode } from "building-types/settings/settingsTypesEnum";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { levelInchesNew, worldInchesNew } from "common/utils/nominalNumbers";
import objectMap from "common/utils/objectMap";
import uid from "common/utils/uid";
import { blockNew } from "doc/elements/blockNew";
import { ENTITY_ID_SEPARATOR } from "doc/entity/componentInstanceEntityResolve";
import { aabb2Center } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { buildPolygonsFromPolyline2, buildPerimeterPolygons, addPolygonsToBSP } from "geom/bsp";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { WindingDir, DIST_EPSILON } from "geom/core";
import { line2Len } from "geom/line2";
import { polyLine2ThickenedEdge, offsetPolyLine2Variable } from "geom/offset";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import {
  planeNormal,
  planeProjectPolyLine3,
  planeEval,
  planeNew,
  vec3PlaneSide,
  PlaneSide,
  planeProject,
  planeDistanceTo,
  planeMoveAlongNormal,
  planeCopy,
} from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2WindingDir,
  polyLine2EdgeCount,
  polyLine2Edge,
  polyLine2Extend,
  edgeIdsByPolyLine2,
  polyLine2AABB2,
  polyLine2NonUniformScaleAndKeepCenter,
  polyLine2Copy,
  polyLine2SetCCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { vec2Sub } from "geom/vec2";
import { levelNew } from "../../elements/level/levelTools";
import type { OpeningPlacementFuncOptionsLiteRoomStartDefined } from "../../elements/openings/openingPlacementFuncOptions";
import { collectRoomConnectionsOnLevel } from "../rooms/collectRoomConnectionsOnLevel";
import { RoomIndexImpl } from "../rooms/roomIndexImpl";
import { roomNew } from "../rooms/roomTools";
import { roomTypeOpenFullAdd } from "../rooms/roomTypeOpenAddFull";
import type { HalfEdgeToWallCutSet } from "../walls/wallCut";
import { mirrorRoofWallSide } from "./roofEdges";
import { isRoofEdgeAlignedToSide } from "./roofSlopes";
import type { RoofWallSystemPerimeter, RoofWallEdge, RoofWallExtension } from "./roofWallSystemTypes";
import { roofWallExtensionNew, roofWallEdgeNew, roofWallSystemPerimeterNew } from "./roofWallSystemTypes";

const WINDOW_TRANSFER_TOLERANCE = 1;

const LEFT_GABLE_FACES: { [key in RoofFaces]?: boolean } = {
  [RoofFaces.LEFT]: true,
  [RoofFaces.LEFT_AND_RIGHT]: true,
  [RoofFaces.ALL]: true,
};

const RIGHT_GABLE_FACES: { [key in RoofFaces]?: boolean } = {
  [RoofFaces.RIGHT]: true,
  [RoofFaces.LEFT_AND_RIGHT]: true,
  [RoofFaces.ALL]: true,
};

const START_GABLE_FACES: { [key in RoofFaces]?: boolean } = {
  [RoofFaces.FRONT]: true,
  [RoofFaces.FRONT_AND_BACK]: true,
  [RoofFaces.ALL]: true,
};

const END_GABLE_FACES: { [key in RoofFaces]?: boolean } = {
  [RoofFaces.BACK]: true,
  [RoofFaces.FRONT_AND_BACK]: true,
  [RoofFaces.ALL]: true,
};

const GABLE_FACES_FROM_GABLE_SIDE: { [key in RoofSide]: { [key in RoofFaces]?: boolean } } = {
  [RoofSide.LEFT]: LEFT_GABLE_FACES,
  [RoofSide.RIGHT]: RIGHT_GABLE_FACES,
  [RoofSide.START]: START_GABLE_FACES,
  [RoofSide.END]: END_GABLE_FACES,
  [RoofSide.INVALID]: {},
};

function getLegacyOffset(edge: RoofEdge, roofType: GableRoofType, legacyRoof: boolean): number {
  const { exteriorWallOffsetWithFinish, exteriorWallOffsetFromOverhangDatum } = edge;
  const isShedRoof = roofType !== GableRoofType.DEFAULT;
  return legacyRoof && isShedRoof ? exteriorWallOffsetWithFinish - exteriorWallOffsetFromOverhangDatum : 0;
}

interface MakeRoofWallSystemMainPerimeterResult {
  readonly wallSystemPerimeter: RoofWallSystemPerimeter;
  readonly roofWallExtensions: RoofWallExtension[];
}

interface MakeRoofWallSystemMainPerimeterOptions {
  readonly perimeters: RoofPerimeters;
  readonly roofType: GableRoofType;
  readonly legacyRoof: boolean;
  readonly extensions: RoofExtensions;
  readonly shedExtensions: ShedRoofExtensions;
  readonly verticalRails: Pick<GableRoofVerticalRails, "startBaseExtension" | "endBaseExtension">;
  readonly exactEdges: RoofEdge[];
}

export function makeRoofWallSystemMainPerimeter({
  perimeters,
  extensions,
  shedExtensions,
  exactEdges,
  verticalRails,
  roofType,
  legacyRoof,
}: MakeRoofWallSystemMainPerimeterOptions): MakeRoofWallSystemMainPerimeterResult {
  const {
    hasStartExtension,
    hasEndExtension,
    hasStartPartialOverlap,
    hasEndPartialOverlap,
    startLeftExtensions,
    startRightExtensions,
    endLeftExtensions,
    endRightExtensions,
  } = extensions;
  const { startBaseExtension, endBaseExtension } = verticalRails;
  const { hasLeftExtension, hasRightExtension, sideExtension } = shedExtensions;
  const { startEdge, boundingPerimeter, exactPerimeter, wallPerimeter } = perimeters;

  const applyStartExtension = hasStartExtension && !hasStartPartialOverlap;
  const applyEndExtension = hasEndExtension && !hasEndPartialOverlap;

  const startExtension = applyStartExtension
    ? Math.max(...startLeftExtensions, ...startRightExtensions, startBaseExtension)
    : 0;
  const endExtension = applyEndExtension ? Math.max(...endLeftExtensions, ...endRightExtensions, endBaseExtension) : 0;
  const leftExtension = hasLeftExtension ? sideExtension : 0;
  const rightExtension = hasRightExtension ? sideExtension : 0;
  const offsetSign = polyLine2WindingDir(exactPerimeter) === WindingDir.CCW ? 1 : -1;

  const roofWallEdges: RoofWallEdge[] = [];
  const roofWallExtensions: RoofWallExtension[] = [];

  const isEdgeAlignedToSide = (edgeIndex: number, side: RoofSide) => {
    return isRoofEdgeAlignedToSide({
      boundingPerimeter,
      exactPerimeter,
      startEdge,
      side,
      edgeIndex,
    });
  };

  const addExtension = (edgeIndex: number, extension: number) => {
    if (extension < DIST_EPSILON) {
      return;
    }

    roofWallExtensions.push(roofWallExtensionNew(edgeIndex, extension));
  };

  const addEdge = (
    refEdge: PolyLine2 | null,
    edgeIndex: number,
    side: RoofSide,
    applyExtension: boolean,
    extension: number,
  ) => {
    const edge = exactEdges[edgeIndex];
    const { side: edgeSide, framingType } = edge;
    const legacyOffset = getLegacyOffset(edge, roofType, legacyRoof);

    const forceAddWall = framingType === RoofWallFramingType.STICK_FRAMING;
    const baseWallType = framingType === RoofWallFramingType.NONE ? WallType.VOID : WallType.SOLID;
    const offset = forceAddWall ? 0 : extension;

    roofWallEdges.push(
      roofWallEdgeNew(
        refEdge,
        applyExtension && !forceAddWall ? WallType.VOID : baseWallType,
        forceAddWall,
        forceAddWall ? side : edgeSide,
        offset + legacyOffset,
      ),
    );

    if (applyExtension && forceAddWall) {
      addExtension(edgeIndex, extension);
    }
  };

  for (let i = 0, l = polyLine2EdgeCount(wallPerimeter); i < l; i++) {
    const { exteriorWallOffsetWithFinish } = exactEdges[i];

    let edge = polyLine2Edge(wallPerimeter, i);

    // we get only positive values for the extensions to avoid flipping the
    // perimeter windind order when offsetting the edges

    if (isEdgeAlignedToSide(i, RoofSide.START)) {
      addEdge(null, i, RoofSide.START, applyStartExtension, Math.max(startExtension, 0));
    } else if (isEdgeAlignedToSide(i, RoofSide.END)) {
      addEdge(null, i, RoofSide.END, applyEndExtension, Math.max(endExtension, 0));
    } else if (isEdgeAlignedToSide(i, RoofSide.LEFT)) {
      edge = polyLine2Extend(
        edge,
        !hasStartExtension ? exteriorWallOffsetWithFinish : 0,
        !hasEndExtension ? exteriorWallOffsetWithFinish : 0,
      );

      addEdge(edge, i, RoofSide.LEFT, hasLeftExtension, Math.max(leftExtension, 0));
    } else if (isEdgeAlignedToSide(i, RoofSide.RIGHT)) {
      edge = polyLine2Extend(
        edge,
        !hasEndExtension ? exteriorWallOffsetWithFinish : 0,
        !hasStartExtension ? exteriorWallOffsetWithFinish : 0,
      );

      addEdge(edge, i, RoofSide.RIGHT, hasRightExtension, Math.max(rightExtension, 0));
    } else {
      addEdge(null, i, RoofSide.INVALID, false, 0);
    }
  }

  const perimeter = offsetPolyLine2Variable(
    exactPerimeter,
    roofWallEdges.map((x) => offsetSign * x.offset),
  );

  return {
    wallSystemPerimeter: roofWallSystemPerimeterNew(perimeter, roofWallEdges),
    roofWallExtensions,
  };
}

export function makeRoofWallSystemPerimeters(roofVolume: GeneratedRoofVolumeLegacy): RoofWallSystemPerimeter[] {
  const { exactEdges, roofType, legacyRoof, extensions, shedExtensions, verticalRails, perimeters } = roofVolume;

  const makeExtensionPerimeter = (
    mainRoomPerimeter: PolyLine2,
    { edgeIndex, extension }: RoofWallExtension,
  ): RoofWallSystemPerimeter => {
    const { side, framingType } = exactEdges[edgeIndex];

    const prevIndex = (edgeIndex + exactEdges.length - 1) % exactEdges.length;
    const nextIndex = (edgeIndex + 1) % exactEdges.length;

    const prevEdge = exactEdges[prevIndex];
    const nextEdge = exactEdges[nextIndex];

    const { framingType: prevFraming, side: prevSide } = prevEdge;
    const { framingType: nextFraming, side: nextSide } = nextEdge;

    const prevLegacyOffset = getLegacyOffset(prevEdge, roofType, legacyRoof);
    const nextLegacyOffset = getLegacyOffset(nextEdge, roofType, legacyRoof);

    const forceAddWall = framingType === RoofWallFramingType.STICK_FRAMING;
    const prevForceAddWall = prevFraming === RoofWallFramingType.STICK_FRAMING;
    const nextForceAddWall = nextFraming === RoofWallFramingType.STICK_FRAMING;

    const extensionPerimeter = polyLine2ThickenedEdge(mainRoomPerimeter, edgeIndex, extension);

    if (!(polyLine2EdgeCount(extensionPerimeter) === 4)) {
      console.assert(false);
    }

    const offsetSign = polyLine2WindingDir(extensionPerimeter) === WindingDir.CCW ? 1 : -1;

    const extensionWallEdges: RoofWallEdge[] = [
      roofWallEdgeNew(null, WallType.SOLID, forceAddWall, side, 0),
      roofWallEdgeNew(null, WallType.SOLID, prevForceAddWall, prevSide, prevLegacyOffset),
      roofWallEdgeNew(null, WallType.VOID, false, RoofSide.INVALID, 0),
      roofWallEdgeNew(null, WallType.SOLID, nextForceAddWall, nextSide, nextLegacyOffset),
    ];

    const offsetExtensionPerimeter = offsetPolyLine2Variable(
      extensionPerimeter,
      extensionWallEdges.map((x) => offsetSign * x.offset),
    );

    return roofWallSystemPerimeterNew(offsetExtensionPerimeter, extensionWallEdges);
  };

  const { wallSystemPerimeter, roofWallExtensions } = makeRoofWallSystemMainPerimeter({
    perimeters,
    roofType,
    legacyRoof,
    extensions,
    shedExtensions,
    verticalRails,
    exactEdges,
  });

  return [
    wallSystemPerimeter,
    ...roofWallExtensions.map((x) => makeExtensionPerimeter(wallSystemPerimeter.perimeter, x)),
  ];
}

/**
 * Uses the roof volume's exact perimeter directly (no per-edge wall offsets or extension polygons).
 */
export function makeRoofWallSystemPerimetersFromExactOutline(
  roofVolume: GeneratedRoofVolumeLegacy,
): RoofWallSystemPerimeter[] {
  const { exactEdges, perimeters } = roofVolume;
  const { exactPerimeter } = perimeters;

  const roofWallEdges = exactEdges.map((edge) => {
    const forceAddWall = edge.framingType === RoofWallFramingType.STICK_FRAMING;
    const wallType = edge.framingType === RoofWallFramingType.NONE ? WallType.VOID : WallType.SOLID;

    return roofWallEdgeNew(null, wallType, forceAddWall, edge.side, 0);
  });

  return [roofWallSystemPerimeterNew(polyLine2Copy(exactPerimeter), roofWallEdges)];
}

function makeRoofRoom(level: Level, block: Block, outline: PolyLine2, type: RoomType): Room {
  const name = ROOF_VIRTUAL_ROOM_NAME;

  return roomNew({
    id: uid(),
    type,
    outline,
    name: name,
    nameInPlan: name,
    level,
    parentId: block.id,
    edgeIds: edgeIdsByPolyLine2(outline),
    ceilingHeightOffset: 0,
    ceilingMaterial: "ceiling0",
    wallMaterial: "wallInterior0",
  });
}

interface MakeRoofRoomsOptions {
  readonly roofLevels: Table<Level>;
  readonly roofLevel: Level;
  readonly roofBlock: Block;
  readonly roofWallPerimeters: RoofWallSystemPerimeter[];
  readonly roofWallMode: RoofWallMode;
}

interface MakeRoofRoomsResult {
  readonly rooms: Table<Room>;
  readonly roomArray: Room[];
  readonly roomLevelIndices: RWTable<number>;
  readonly roomsByLevel: RWTable<string[]>;
}

export function makeRoofRooms({
  roofLevels,
  roofLevel,
  roofBlock,
  roofWallPerimeters,
  roofWallMode,
}: MakeRoofRoomsOptions): MakeRoofRoomsResult {
  const rooms: RWTable<Room> = {};
  const roomArray: Room[] = [];
  const roomLevelIndices: RWTable<number> = {};
  const roomsByLevel: RWTable<string[]> = objectMap(roofLevels, () => []);

  let roomLevelIndex = 0;
  for (const roofWallPerimeter of roofWallPerimeters) {
    const { perimeter } = roofWallPerimeter;
    const mainRoomType = roofWallMode === RoofWallMode.EXTERIOR ? RoomType.PORTICO : RoomType.GENERAL;
    const roomType = roomLevelIndex === 0 ? mainRoomType : RoomType.GENERAL;

    const room = makeRoofRoom(roofLevel, roofBlock, perimeter, roomType);

    roomArray.push(room);
    rooms[room.id] = room;
    roomsByLevel[roofLevel.id].push(room.id);
    roomLevelIndices[room.id] = roomLevelIndex;

    roomLevelIndex++;
  }

  if (!(roomArray.length === roofWallPerimeters.length)) {
    console.assert(false);
  }

  return {
    rooms,
    roomArray,
    roomLevelIndices,
    roomsByLevel,
  };
}

export function makeRoofBlock(level: Level): Block {
  return blockNew({
    id: uid(),
    name: "Roof Block",
    sortIndex: 0,
    levelId: level.id,
  });
}

export function makeRoofWallLevel(floorElevation: number, wallTop: number, roofBottom: number, roofTop: number): Level {
  const levelDef: LevelDef = {
    entityType: "LEVEL",
    id: uid(),
    name: "Temporary Roof Level",
    topOfWall: levelInchesNew(wallTop),
    sortIndex: 0,
    enableAutoGeneratedFoundation: true,
    enableAutoGeneratedFloorSystem: true,
    placement: stackedLevelPlacementNew(),
  };

  const levelDatumsGenerated: GeneratedLevelDatums = {
    instanceOffset: worldInchesNew(0),
    grade: levelInchesNew(0),
    garage: levelInchesNew(0),
    porch: levelInchesNew(0),
    deck: levelInchesNew(0),
    wainscot: levelInchesNew(0),
    foundation: levelInchesNew(0),
    roofBottom: levelInchesNew(roofBottom),
    roofTop: levelInchesNew(roofTop),
  };

  // setting the floor index to 1 to avoid the bottom of the wall to be moved up
  // by the foundation settings
  return levelNew({
    ...levelDef,
    topOfWall: levelInchesNew(wallTop),
    floorIndex: 1,
    floorElevation: worldInchesNew(floorElevation),
    levelDatumsGenerated,
    floorPlateThickness: 0,
    floorAssemblyId: uid(),
  });
}

export function makeRoofLabelHalfEdgeMaps(
  roomArray: Room[],
  planarGraph: PolygonPlanarGraph,
  roofWallPerimeters: RoofWallSystemPerimeter[],
  roofLabels: GableLabel[],
  peakDirection: RoofPeakDirection | undefined,
  mirror: boolean,
  rootInstanceId?: string,
): HalfEdgeAssociationTable<Label> {
  if (!(roofWallPerimeters.length === roomArray.length)) {
    console.assert(false);
  }

  const roofLevelLabels: RWHalfEdgeAssociationTable<Label> = {};

  for (let roomIndex = 0, roomCount = roomArray.length; roomIndex < roomCount; roomIndex++) {
    const { outline, id: roomId } = roomArray[roomIndex];
    const { roofWallEdges } = roofWallPerimeters[roomIndex];

    for (let wallIndex = 0, wallCount = polyLine2EdgeCount(outline); wallIndex < wallCount; wallIndex++) {
      let { roofSide } = roofWallEdges[wallIndex];
      roofSide = mirrorRoofWallSide(roofSide, mirror, peakDirection);
      const validGableFaces = GABLE_FACES_FROM_GABLE_SIDE[roofSide];

      const labels = roofLabels
        .filter((l) => {
          const face = l.face ?? RoofFaces.FRONT_AND_BACK;
          return validGableFaces[face];
        })
        .map((l) => {
          const facadeId =
            rootInstanceId !== undefined ? `${l.facadeId}${ENTITY_ID_SEPARATOR}${rootInstanceId}` : l.facadeId;
          return {
            roomId,
            outside: true,
            precedence: LabelPrecedence.Room,
            item: {
              facadeId,
            },
          };
        });

      if (labels.length === 0) {
        continue;
      }

      const subwalls = planarGraph.polygonToPlanarGraph[roomIndex][wallIndex];

      // for each sub-wall of wall
      for (let subWallIndex = 0, subWallCount = subwalls.length; subWallIndex < subWallCount; subWallIndex++) {
        const { index, edgeIndex } = subwalls[subWallIndex];
        const halfEdgeIndex = planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];

        roofLevelLabels[halfEdgeIndex] = labels;
      }
    }
  }

  return roofLevelLabels;
}

interface RoofWallCutHalfEdgeMapResult {
  readonly halfEdgeToWallCutSet: HalfEdgeToWallCutSet;
  readonly extraWallCutBSPs: NodeBSP[];
}

export function makeRoofWallCutHalfEdgeMap(
  planarGraph: PolygonPlanarGraph,
  roofLevel: Level,
  roofWallCuts: WallOpening[],
): RoofWallCutHalfEdgeMapResult {
  const halfEdgeToWallCutSet: HalfEdgeToWallCutSet = {};
  const extraWallCutBSPs: NodeBSP[] = [];
  const { halfEdges, vertexPositions } = planarGraph.planarGraph;
  const { floorElevation, levelDatumsGenerated } = roofLevel;
  const { roofTop } = levelDatumsGenerated;

  function addWallCutToEdge(halfEdgeIndex: number, wallCut: WallCut) {
    if (!halfEdgeToWallCutSet[halfEdgeIndex]) {
      halfEdgeToWallCutSet[halfEdgeIndex] = [];
    }

    halfEdgeToWallCutSet[halfEdgeIndex].push(wallCut);
  }

  function tryToAddRoofWallCut(roofWallCut: WallOpening) {
    const { frontPlane, backPlane } = roofWallCut;
    const frontOutline: PolyLine3 | undefined = wallOpeningOpening3(
      roofWallCut,
      "FRONT",
      roofWallCut.hasExteriorCasing,
    );

    let { originX: frontOriginX, originY: frontOriginY, originZ: frontOriginZ } = frontPlane;
    let { originX: backOriginX, originY: backOriginY, originZ: backOriginZ } = backPlane;

    const [frontNormalX, frontNormalY, frontNormalZ] = planeNormal(frontPlane);
    const [backNormalX, backNormalY, backNormalZ] = planeNormal(backPlane);

    // apply tolerance
    frontOriginX += frontNormalX * WINDOW_TRANSFER_TOLERANCE;
    frontOriginY += frontNormalY * WINDOW_TRANSFER_TOLERANCE;
    frontOriginZ += frontNormalZ * WINDOW_TRANSFER_TOLERANCE;

    backOriginX += backNormalX * WINDOW_TRANSFER_TOLERANCE;
    backOriginY += backNormalY * WINDOW_TRANSFER_TOLERANCE;
    backOriginZ += backNormalZ * WINDOW_TRANSFER_TOLERANCE;

    assertIsNotNullOrUndefined(frontOutline);

    // build the refence point and get the opening size
    const outline = planeProjectPolyLine3(frontPlane, frontOutline);
    const aabb = polyLine2AABB2(outline);
    const width = aabb2Width(aabb);
    const halfWidth = width * 0.5;
    const height = aabb2Height(aabb);
    const [centerX, centerY] = aabb2Center(aabb);
    const [bottomCenterX, bottomCenterY] = vec2Sub(centerX, centerY, 0, height * 0.5);
    const [refX, refY, refZ] = planeEval(frontPlane, bottomCenterX, bottomCenterY);

    // if the wall cut is completely above the roof or below the floor, discard it
    if (refZ + height < floorElevation + DIST_EPSILON || refZ > floorElevation + roofTop - DIST_EPSILON) {
      return;
    }

    // iterate half edges
    const halfEdgeVisited: { [halfEdge: number]: boolean } = {};
    for (let i = 0, l = halfEdges.length; i < l; i += 2) {
      const leftHalfEdge = i / 2;
      const rightHalfEdge = planarGraphAdjacentHalfEdge(leftHalfEdge);

      if (halfEdgeVisited[leftHalfEdge] || halfEdgeVisited[rightHalfEdge]) {
        continue;
      }

      halfEdgeVisited[leftHalfEdge] = true;
      halfEdgeVisited[rightHalfEdge] = true;

      // get the edge end points
      const v0 = halfEdges[leftHalfEdge * 2];
      const v1 = halfEdges[leftHalfEdge * 2 + 1];

      const startX = vertexPositions[v0 * 2];
      const startY = vertexPositions[v0 * 2 + 1];

      const endX = vertexPositions[v1 * 2];
      const endY = vertexPositions[v1 * 2 + 1];

      const edgeLength = line2Len(startX, startY, endX, endY);

      if (edgeLength < DIST_EPSILON) {
        continue;
      }

      // build the edge plane
      let [tX, tY] = vec2Sub(endX, endY, startX, startY);
      tX /= edgeLength;
      tY /= edgeLength;

      const edgePlane = planeNew(startX, startY, floorElevation, tX, tY, 0, 0, 0, 1);

      // check it the opening is affecting this edge
      const startSide1 = vec3PlaneSide(
        startX,
        startY,
        0,
        frontOriginX,
        frontOriginY,
        frontOriginZ,
        frontNormalX,
        frontNormalY,
        frontNormalZ,
      );
      const startSide2 = vec3PlaneSide(
        startX,
        startY,
        0,
        backOriginX,
        backOriginY,
        backOriginZ,
        backNormalX,
        backNormalY,
        backNormalZ,
      );

      const endSide1 = vec3PlaneSide(
        endX,
        endY,
        0,
        frontOriginX,
        frontOriginY,
        frontOriginZ,
        frontNormalX,
        frontNormalY,
        frontNormalZ,
      );
      const endSide2 = vec3PlaneSide(
        endX,
        endY,
        0,
        backOriginX,
        backOriginY,
        backOriginZ,
        backNormalX,
        backNormalY,
        backNormalZ,
      );

      if (
        startSide1 === PlaneSide.FRONT ||
        startSide2 === PlaneSide.FRONT ||
        endSide1 === PlaneSide.FRONT ||
        endSide2 === PlaneSide.FRONT
      ) {
        continue;
      }

      // reproject the refrence point on the edge plane
      const [projX, projY] = planeProject(edgePlane, refX, refY, refZ);
      const left = projX - halfWidth;
      const right = projX + halfWidth;
      const bottom = projY;
      const top = projY + height;

      // discard if the projection is outside the edge
      if (right < 0 || left > edgeLength) {
        continue;
      }

      // special case when the opening has overlap with the edge, but the opening
      // extends beyond the edge
      if (right > edgeLength || left < 0 || bottom < 0 || top > roofTop) {
        const wallCutBSP = makeRoofWallCutsBSP(roofWallCut);
        if (wallCutBSP) {
          extraWallCutBSPs.push(wallCutBSP);
        }

        continue;
      }

      const edgeParam = (right + left) * 0.5;

      addWallCutToEdge(leftHalfEdge, {
        type: "WALL_CUT",
        edgeParam: edgeLength - edgeParam,
        edgeParamNormalized: false,
        edgeParamAbsolute: true,
        width,
        height,
        bottom,
        opening: roofWallCut,
      });
    }
  }

  for (const roofWallCut of roofWallCuts) {
    tryToAddRoofWallCut(roofWallCut);
  }

  return { halfEdgeToWallCutSet, extraWallCutBSPs };
}

export function makeRoofWallCutsBSP(roofWallCut: WallOpening): NodeBSP | null {
  const { frontPlane, backPlane } = roofWallCut;
  const dist = planeDistanceTo(frontPlane, backPlane.originX, backPlane.originY, backPlane.originZ);

  if (dist < DIST_EPSILON) {
    return null;
  }

  let frontOutline2 = wallOpeningOpening2(roofWallCut, "FRONT");
  let backOutline2 = wallOpeningOpening2(roofWallCut, "BACK");

  const polygons: PolygonBSP[] = [];

  if (wallOpeningShouldMirror(roofWallCut)) {
    //Mirror the polylines
    frontOutline2 = polyLine2NonUniformScaleAndKeepCenter(polyLine2Copy(frontOutline2), -1, 1);
    backOutline2 = polyLine2NonUniformScaleAndKeepCenter(polyLine2Copy(backOutline2), -1, 1);
    //Correct the winding orientation
    frontOutline2 = profile2OrientCopy(polyLine2SetCCW(polyLine2Copy(frontOutline2)));
    backOutline2 = profile2OrientCopy(polyLine2SetCCW(polyLine2Copy(backOutline2)));
  }

  // caps
  buildPolygonsFromPolyline2(frontOutline2, frontPlane, 0, polygons);
  buildPolygonsFromPolyline2(backOutline2, backPlane, 0, polygons);

  // edge polygons
  const frontPlaneMoved = planeMoveAlongNormal(planeCopy(frontPlane), -dist);
  polygons.push(...buildPerimeterPolygons(frontOutline2, frontPlaneMoved, dist));

  return addPolygonsToBSP(null, null, polygons);
}

interface MakeRoofRoomsConnectionsOptions {
  readonly roofLevel: Level;
  readonly rooms: Table<Room>;
  readonly roomArray: Room[];
  readonly roomLevelIndices: RWTable<number>;
  readonly roomsByLevel: Table<string[]>;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly roofWallPerimeters: RoofWallSystemPerimeter[];
}

export function makeRoofRoomsConnections({
  roofLevel,
  rooms,
  roomArray,
  roomLevelIndices,
  roomsByLevel,
  planarGraphs,
  roofWallPerimeters,
}: MakeRoofRoomsConnectionsOptions): RoomConnection[][] {
  const openFullDoors: LevelOpenFullDoors = {};
  const roomIndex = new RoomIndexImpl(rooms, roomLevelIndices, roomsByLevel, planarGraphs);
  const planarGraph = planarGraphs[roofLevel.id];

  if (!planarGraph) {
    return [];
  }

  for (let roomLevelIndex = 0, roomCount = roomArray.length; roomLevelIndex < roomCount; roomLevelIndex++) {
    const { roofWallEdges } = roofWallPerimeters[roomLevelIndex];
    const room = roomArray[roomLevelIndex];

    // for each room wall
    for (let wallIndex = 0, wallCount = roofWallEdges.length; wallIndex < wallCount; wallIndex++) {
      if (roofWallEdges[wallIndex].wallType !== WallType.VOID) {
        continue;
      }

      const subwalls = planarGraph.polygonToPlanarGraph[roomLevelIndex][wallIndex];

      // for each sub-wall of wall
      for (let subWallIndex = 0, subWallCount = subwalls.length; subWallIndex < subWallCount; subWallIndex++) {
        const { index, edgeIndex } = subwalls[subWallIndex];
        const halfEdgeIndex = planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];

        // fully open exterior walls
        const placementOptions: OpeningPlacementFuncOptionsLiteRoomStartDefined = {
          roomIndex,
          roomStart: room,
          wallIndex,
          halfEdgeIndex,
          planarGraph,
          perimeterStreetOrientationTable: {},
        };

        roomTypeOpenFullAdd(placementOptions, openFullDoors, roofLevel.id);
      }
    }
  }

  // room connections
  return collectRoomConnectionsOnLevel({
    openFullDoors,
    levelId: roofLevel.id,
    roomsByLevel,
    roomLevelIndices,
  });
}

interface MakeRoofWallSystemPerimeterOptions {
  readonly roof: GeneratedRoof;
  readonly wallSystem: WallSystem;
  readonly planarGraph: PolygonPlanarGraph;
  readonly roofWallPerimeters: RoofWallSystemPerimeter[];
}

export function makeRoofWallSystemEdgeMap({
  roof,
  wallSystem,
  planarGraph,
  roofWallPerimeters,
}: MakeRoofWallSystemPerimeterOptions): Table<RoofWallSystemEdge> {
  const { walls } = wallSystem;
  const { roofVolume } = roof;
  const { startFaces, endFaces } = roofVolume;
  const { face: startFace } = startFaces;
  const { face: endFace } = endFaces;

  const edges: RWTable<RoofWallSystemEdge> = {};
  for (const wallId in walls) {
    const wall = walls[wallId];

    const path = getWallPath(wall);
    const { halfEdgeIndex } = path;

    const edgeRef = planarGraph.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex);

    if (!edgeRef) {
      continue;
    }

    const { index, edgeIndex } = edgeRef;

    if (index >= roofWallPerimeters.length) {
      continue;
    }

    const { roofWallEdges } = roofWallPerimeters[index];

    if (edgeIndex >= roofWallEdges.length) {
      continue;
    }

    const { roofSide, edge } = roofWallEdges[edgeIndex];
    let referenceFace: Face | null = null;
    let referenceEdge: PolyLine2 | null = null;

    if (roofSide === RoofSide.START) {
      referenceFace = startFace;
    } else if (roofSide === RoofSide.END) {
      referenceFace = endFace;
    } else if (roofSide === RoofSide.LEFT) {
      referenceEdge = edge;
    } else if (roofSide === RoofSide.RIGHT) {
      referenceEdge = edge;
    }

    edges[wallId] = {
      referenceFace,
      referenceEdge,
    };
  }

  return edges;
}
